import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useDensity } from '../context/DensityContext';
import {
  LayoutDashboard,
  FileCheck,
  LogOut,
  FileText,
  Target,
  BarChart3,
  CalendarCheck,
  Shield,
  Building2,
  ChevronDown,
  ArrowLeftRight,
  Crown,
  Sun,
  Moon,
  Monitor,
  Rows3,
  Layers,
  Activity,
  ClipboardCheck,
  UserCircle,
  Bell,
  Users,
  PieChart,
  CreditCard,
} from 'lucide-react';
import { getResolvedNavFeatures } from '../utils/navFeatures';
import { Avatar } from './ui';
import NotificationBell from './NotificationBell';

const DEFAULT_PRODUCT_BRAND = {
  prefix: 'ISO',
  suffix: '27001',
};

function getProductBrand(company) {
  const raw = company?.product_brand?.trim?.() || company?.settings?.product_brand?.trim?.();
  if (!raw) return DEFAULT_PRODUCT_BRAND;
  const parts = raw.split(/\s+/);
  if (parts.length === 1) return { prefix: parts[0], suffix: '' };
  return {
    prefix: parts.slice(0, -1).join(' '),
    suffix: parts[parts.length - 1],
  };
}

const Navigation = () => {
  const {
    user,
    company,
    companies,
    activeCompanyId,
    isSuperadmin,
    isExternalAuditor,
    isGuest,
    logout,
    switchCompany,
  } = useAuth();
  const { mode: themeMode, resolved: themeResolved, setMode: setThemeMode } = useTheme();
  const { density, toggle: toggleDensity } = useDensity();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const switcherRef = useRef(null);
  const themeRef = useRef(null);
  const userMenuRef = useRef(null);
  const moreMenuRef = useRef(null);

  useEffect(() => {
    setUserMenuOpen(false);
    setCompanySwitcherOpen(false);
    setThemeMenuOpen(false);
    setMoreMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!userMenuOpen && !companySwitcherOpen && !themeMenuOpen && !moreMenuOpen) return;
    const handlePointerDownOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (switcherRef.current && !switcherRef.current.contains(e.target)) {
        setCompanySwitcherOpen(false);
      }
      if (themeRef.current && !themeRef.current.contains(e.target)) {
        setThemeMenuOpen(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setMoreMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDownOutside);
    return () => document.removeEventListener('pointerdown', handlePointerDownOutside);
  }, [userMenuOpen, companySwitcherOpen, themeMenuOpen, moreMenuOpen]);

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === path;
    if (path === '/admin') return location.pathname.startsWith('/admin');
    if (path === '/profile') return location.pathname.startsWith('/profile');
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isOnAdminPage = location.pathname.startsWith('/admin');

  const navFeatures = getResolvedNavFeatures(company);

  const appNavItems = isExternalAuditor || isGuest
    ? [
        { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
        { path: '/policies', label: t('nav.policies'), icon: FileText },
      ]
    : [
        { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
        { path: '/policies', label: t('nav.policies'), icon: FileText },
        { path: '/activities', label: t('nav.activities'), icon: CalendarCheck },
        { path: '/risks', label: t('nav.risks', 'Risks'), icon: Shield },
        { path: '/readiness', label: t('nav.readiness', 'Readiness'), icon: ClipboardCheck },
        { path: '/reports', label: t('nav.reports', 'Reports'), icon: PieChart },
      ];

  const moreNavItems = isExternalAuditor || isGuest
    ? []
    : [
        ...(navFeatures.audit ? [{ path: '/audit', label: t('nav.audit'), icon: FileCheck }] : []),
        ...(navFeatures.programs ? [{ path: '/audit-programs', label: t('nav.programs'), icon: Target }] : []),
        { path: '/vendors', label: t('nav.vendors', 'Vendors'), icon: Building2 },
        { path: '/employees', label: t('nav.employees', 'Employees'), icon: Users },
        { path: '/frameworks', label: t('nav.frameworks', 'Frameworks'), icon: Layers },
        { path: '/evidence', label: t('nav.evidence', 'Evidence'), icon: Activity },
        ...(navFeatures.performance ? [{ path: '/performance', label: t('nav.performance'), icon: BarChart3 }] : []),
      ];

  const handleCompanySwitch = (companyId) => {
    switchCompany(companyId);
    setCompanySwitcherOpen(false);
    navigate('/dashboard');
  };

  const handleGoToAdmin = () => {
    switchCompany(null);
    setCompanySwitcherOpen(false);
    navigate('/admin');
  };

  const showTenantAppNav = !isSuperadmin || (isSuperadmin && activeCompanyId && !isOnAdminPage);

  const productBrand = getProductBrand(company);

  const ThemeIcon = themeMode === 'system' ? Monitor : themeResolved === 'dark' ? Moon : Sun;

  return (
    <nav
      aria-label="Primary"
      className="sticky top-0 z-[90] w-full max-w-full border-b border-neutral-200 bg-white shadow-sm transition-colors dark:border-neutral-800 dark:bg-neutral-900"
    >
      <div className="max-w-7xl mx-auto min-w-0 w-full px-4 sm:px-6 lg:px-8">
        <div className="flex min-h-16 min-w-0 w-full items-center justify-between gap-2 py-1.5 lg:min-h-16 lg:py-0">
          <div className="flex min-w-0 flex-1 items-center">
            <Link
              to={isSuperadmin && !activeCompanyId ? '/admin' : '/dashboard'}
              className="flex shrink-0 items-center gap-2 min-w-0"
            >
              {showTenantAppNav && company?.branding_logo_url ? (
                <img
                  src={company.branding_logo_url}
                  alt=""
                  crossOrigin="anonymous"
                  className="h-9 w-auto max-w-[140px] object-contain"
                />
              ) : (
                <Shield className="h-6 w-6 text-brand-600 dark:text-brand-400 shrink-0" aria-hidden="true" />
              )}
              <span className="text-lg font-bold text-neutral-900 dark:text-neutral-100 tracking-tight truncate">
                {productBrand.prefix}
                {productBrand.suffix && (
                  <>
                    {' '}
                    <span className="text-brand-600 dark:text-brand-400">{productBrand.suffix}</span>
                  </>
                )}
              </span>
            </Link>

            {isSuperadmin && (
              <div className="ml-2 flex shrink-0 items-center sm:ml-3" ref={switcherRef}>
                <div className="relative">
                  <button
                    onClick={() => setCompanySwitcherOpen(!companySwitcherOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 dark:border-indigo-900/50 dark:bg-indigo-900/40 dark:text-indigo-200 dark:border-indigo-800 dark:hover:bg-indigo-900/60 transition-colors"
                  >
                    {activeCompanyId ? (
                      <>
                        <Building2 className="w-3.5 h-3.5" aria-hidden="true" />
                        <span className="max-w-[140px] truncate">{company?.name || 'Organisation'}</span>
                      </>
                    ) : (
                      <>
                        <Crown className="w-3.5 h-3.5" aria-hidden="true" />
                        <span>{t('nav.admin')} Panel</span>
                      </>
                    )}
                    <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>

                  {companySwitcherOpen && (
                    <div className="absolute top-full left-0 mt-1 w-72 bg-white dark:bg-neutral-900 rounded-xl shadow-popover border border-neutral-200 dark:border-neutral-800 py-2 z-50 max-h-96 overflow-y-auto">
                      <button
                        onClick={handleGoToAdmin}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-indigo-50 hover:dark:bg-indigo-950/30 dark:hover:bg-indigo-900/30 transition-colors ${ !activeCompanyId ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200 font-medium' : 'text-neutral-700 dark:text-neutral-300' }`}
                      >
                        <Crown className="w-4 h-4 text-indigo-600 dark:text-indigo-300" aria-hidden="true" />
                        <div>
                          <div className="font-medium">{t('nav.admin')} Panel</div>
                          <div className="text-xs text-neutral-500 dark:text-neutral-400">Manage all customers</div>
                        </div>
                      </button>
                      <div className="border-t border-neutral-100 dark:border-neutral-800 my-1" />
                      <div className="px-4 py-1.5 text-xs font-medium text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                        {t('nav.organisations')}
                      </div>
                      {companies.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-neutral-500 dark:text-neutral-400">No customers yet</div>
                      ) : companies.map(c => (
                        <button
                          key={c.id}
                          onClick={() => handleCompanySwitch(c.id)}
                          className={`w-full flex items-center gap-3 px-4 py-2 text-left text-sm hover:bg-neutral-50 hover:dark:bg-neutral-900/60 dark:hover:bg-neutral-800 transition-colors ${ activeCompanyId === c.id ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-200 font-medium' : 'text-neutral-700 dark:text-neutral-300' }`}
                        >
                          <Building2 className="w-4 h-4 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
                          <div>
                            <div>{c.name}</div>
                            <div className="text-xs text-neutral-400 dark:text-neutral-500">{c.industry || c.size}</div>
                          </div>
                          {activeCompanyId === c.id && (
                            <span className="ml-auto text-xs bg-brand-100 text-brand-700 dark:bg-brand-900/50 dark:text-brand-200 px-1.5 py-0.5 rounded">
                              Active
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="hidden flex-1 items-center gap-x-0.5 lg:ml-4 lg:flex">
              {isSuperadmin && !activeCompanyId && (
                <Link
                  to="/admin"
                  className={`inline-flex shrink-0 items-center whitespace-nowrap border-b-2 px-3 pb-0.5 pt-1 text-sm font-medium transition-colors ${ isActive('/admin') ? 'border-indigo-500 text-indigo-600 dark:text-indigo-300' : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:dark:border-neutral-700 hover:text-neutral-700 hover:dark:text-neutral-300 dark:text-neutral-400 dark:hover:text-neutral-200' }`}
                >
                  <Crown className="h-4 w-4 mr-1.5" aria-hidden="true" />
                  {t('nav.organisations')}
                </Link>
              )}
              {showTenantAppNav && appNavItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex shrink-0 items-center whitespace-nowrap border-b-2 px-3 pb-0.5 pt-1 text-sm font-medium transition-colors ${ active ? 'border-brand-500 text-brand-600 dark:text-brand-300' : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:dark:border-neutral-700 hover:text-neutral-700 hover:dark:text-neutral-300 dark:text-neutral-400 dark:hover:text-neutral-200' }`}
                  >
                    <Icon className="h-4 w-4 mr-1.5" aria-hidden="true" />
                    {item.label}
                  </Link>
                );
              })}
              {showTenantAppNav && moreNavItems.length > 0 && (
                <div className="relative shrink-0" ref={moreMenuRef}>
                  <button
                    type="button"
                    onClick={() => setMoreMenuOpen((o) => !o)}
                    className={`inline-flex items-center whitespace-nowrap border-b-2 px-3 pb-0.5 pt-1 text-sm font-medium transition-colors ${
                      moreNavItems.some((i) => isActive(i.path))
                        ? 'border-brand-500 text-brand-600 dark:text-brand-300'
                        : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 dark:hover:border-neutral-700'
                    }`}
                    aria-haspopup="menu"
                    aria-expanded={moreMenuOpen}
                  >
                    More
                    <ChevronDown className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                  {moreMenuOpen && (
                    <div
                      role="menu"
                      className="absolute left-0 top-full z-50 mt-1 w-48 rounded-xl border border-neutral-200 bg-white py-1.5 shadow-lg dark:border-neutral-800 dark:bg-neutral-900"
                    >
                      {moreNavItems.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.path);
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            role="menuitem"
                            className={`flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                              active
                                ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-200'
                                : 'text-neutral-700 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-800'
                            }`}
                          >
                            <Icon className="h-4 w-4 shrink-0 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            {/* Density toggle */}
            <button
              type="button"
              onClick={toggleDensity}
              className="p-2 rounded-lg text-neutral-500 hover:text-neutral-700 hover:dark:text-neutral-300 dark:text-neutral-400 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
              aria-label={t('nav.toggleDensity')}
              title={`${t('nav.toggleDensity')} (${density})`}
            >
              <Rows3 className="w-4 h-4" aria-hidden="true" />
            </button>

            {/* Notification bell */}
            {showTenantAppNav && <NotificationBell />}

            {/* Theme toggle */}
            <div className="relative" ref={themeRef}>
              <button
                type="button"
                onClick={() => setThemeMenuOpen(!themeMenuOpen)}
                className="p-2 rounded-lg text-neutral-500 hover:text-neutral-700 hover:dark:text-neutral-300 dark:text-neutral-400 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                aria-label={t('nav.toggleTheme')}
                aria-haspopup="menu"
                aria-expanded={themeMenuOpen}
                title={t('nav.toggleTheme')}
              >
                <ThemeIcon className="w-4 h-4" aria-hidden="true" />
              </button>
              {themeMenuOpen && (
                <div
                  role="menu"
                  className="absolute top-full right-0 mt-1 w-36 bg-white dark:bg-neutral-900 rounded-lg shadow-popover border border-neutral-200 dark:border-neutral-800 py-1 z-50"
                >
                  {[
                    { value: 'light', label: 'Light', icon: Sun },
                    { value: 'dark', label: 'Dark', icon: Moon },
                    { value: 'system', label: 'System', icon: Monitor },
                  ].map((opt) => {
                    const Icon = opt.icon;
                    const active = themeMode === opt.value;
                    return (
                      <button
                        key={opt.value}
                        role="menuitemradio"
                        aria-checked={active}
                        onClick={() => {
                          setThemeMode(opt.value);
                          setThemeMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${ active ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-200' : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 hover:dark:bg-neutral-900/60 dark:hover:bg-neutral-800' }`}
                      >
                        <Icon className="w-4 h-4" aria-hidden="true" />
                        <span>{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {isSuperadmin && activeCompanyId && (
              <button
                onClick={handleGoToAdmin}
                className="flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/30 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 px-2.5 py-1.5 rounded-lg transition-colors border border-indigo-200 dark:border-indigo-900/50 dark:border-indigo-800"
                title="Back to admin panel"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" aria-hidden="true" />
                {t('nav.admin')}
              </button>
            )}
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setUserMenuOpen((o) => !o)}
                className="flex max-w-[200px] sm:max-w-[240px] items-center gap-2 rounded-full border border-neutral-200 bg-neutral-100 py-1 pl-1 pr-2 text-sm text-neutral-600 transition-colors hover:bg-neutral-200 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700 sm:pr-3"
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                aria-label={t('nav.accountMenu', 'Account menu')}
              >
                <Avatar
                  size="xs"
                  name={[user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email}
                />
                <span className="min-w-0 flex-1 truncate text-left">{user?.email}</span>
                {isSuperadmin && (
                  <span className="shrink-0 text-[10px] bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-200 px-1.5 py-0.5 rounded-full font-medium uppercase">
                    SA
                  </span>
                )}
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-neutral-400 dark:text-neutral-500" aria-hidden />
              </button>
              {userMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-50 mt-1 w-52 rounded-lg border border-neutral-200 bg-white py-1 shadow-popover dark:border-neutral-800 dark:bg-neutral-900"
                >
                  {!isExternalAuditor && !isGuest && (
                    <>
                      <Link
                        to="/profile"
                        role="menuitem"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-800"
                      >
                        <UserCircle className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden />
                        Profile
                      </Link>
                      <Link
                        to="/billing"
                        role="menuitem"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-800"
                      >
                        <CreditCard className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden />
                        Plans & Billing
                      </Link>
                      <div className="my-1 border-t border-neutral-100 dark:border-neutral-800" />
                    </>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setUserMenuOpen(false);
                      logout();
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-950/40"
                  >
                    <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                    {t('nav.signOut')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Primary links on small screens. */}
      <div className="border-t border-neutral-200 bg-neutral-50/80 dark:border-neutral-800 dark:bg-neutral-900/80 lg:hidden">
        <div className="max-w-7xl mx-auto overflow-x-auto px-2 py-2 sm:px-4">
          <div className="flex w-max min-w-full items-center gap-1 pb-0.5">
            {isSuperadmin && !activeCompanyId && (
              <Link
                to="/admin"
                className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium sm:text-sm ${ isActive('/admin') ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200' : 'text-neutral-600 hover:bg-neutral-200 dark:text-neutral-400 dark:hover:bg-neutral-800' }`}
              >
                {t('nav.organisations')}
              </Link>
            )}
            {showTenantAppNav &&
              [...appNavItems, ...moreNavItems].map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium sm:text-sm ${ active ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-200' : 'text-neutral-600 hover:bg-neutral-200 dark:text-neutral-400 dark:hover:bg-neutral-800' }`}
                  >
                    <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                    {item.label}
                  </Link>
                );
              })}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
